import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { notification } from "antd";
import JurnalTemplateService from "services/Setup/Jurnal/JurnalTemplateService";

export const initialState = {
  loading: false,
  error: null,
  jurnalTemplateList: [],
  jurnalTemplateCount: 0,
  selectedJurnalTemplate: null,
};

const toListAndCount = (response) => {
  const payload = response?.data;
  if (payload && Array.isArray(payload.data)) {
    return { list: payload.data, count: payload.count ?? payload.data.length };
  }
  if (Array.isArray(payload)) return { list: payload, count: payload.length };
  return { list: [], count: 0 };
};

export const getJurnalTemplateList = createAsyncThunk(
  "jurnalTemplate/getJurnalTemplateList",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await JurnalTemplateService.getJurnalTemplateList(params);
      return toListAndCount(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getJurnalTemplateById = createAsyncThunk(
  "jurnalTemplate/getJurnalTemplateById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await JurnalTemplateService.getJurnalTemplateById(id);
      return response?.data ?? null;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const addJurnalTemplate = createAsyncThunk(
  "jurnalTemplate/addJurnalTemplate",
  async (data, { rejectWithValue }) => {
    try {
      const response = await JurnalTemplateService.addJurnalTemplate(data);

      if (response.status === 200) {
        const body = response.data;
        notification.success({ message: body.status, description: body.message, duration: 3 });
        return body;
      }

      notification.error({ message: "Error", description: "Unexpected response!", duration: 3 });
      return rejectWithValue(response?.data);
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.response?.data?.message || "Gagal menyimpan jurnal template!",
        duration: 3,
      });
      return rejectWithValue(error.response?.data);
    }
  }
);

export const updateJurnalTemplate = createAsyncThunk(
  "jurnalTemplate/updateJurnalTemplate",
  async (data, { rejectWithValue }) => {
    try {
      const response = await JurnalTemplateService.updateJurnalTemplate(data);

      if (response.status === 200) {
        notification.success({
          message: response.data.status,
          description: response.data.message,
          duration: 3,
        });
        return response.data;
      }

      return rejectWithValue(response.data);
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.response?.data?.message || "Gagal memperbarui jurnal template!",
      });
      return rejectWithValue(error.response?.data);
    }
  }
);

export const deleteJurnalTemplate = createAsyncThunk(
  "jurnalTemplate/deleteJurnalTemplate",
  async (data, { rejectWithValue }) => {
    try {
      const response = await JurnalTemplateService.deleteJurnalTemplate(data);

      if (response.status === 200) {
        notification.success({
          message: response.data.status,
          description: response.data.message,
        });
        return response.data;
      }

      return rejectWithValue(response.data);
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.response?.data?.message || "Gagal menghapus jurnal template!",
      });
      return rejectWithValue(error.response?.data);
    }
  }
);

const JurnalTemplateSlice = createSlice({
  name: "jurnalTemplate",
  initialState,
  reducers: {
    clearSelectedJurnalTemplate: (state) => {
      state.selectedJurnalTemplate = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getJurnalTemplateList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getJurnalTemplateList.fulfilled, (state, action) => {
        state.loading = false;
        state.jurnalTemplateList = action.payload.list;
        state.jurnalTemplateCount = action.payload.count;
      })
      .addCase(getJurnalTemplateList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Gagal memuat daftar jurnal template";
      })

      .addCase(getJurnalTemplateById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getJurnalTemplateById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedJurnalTemplate = action.payload;
      })
      .addCase(getJurnalTemplateById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Gagal memuat detail jurnal template";
      })

      .addCase(addJurnalTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(addJurnalTemplate.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addJurnalTemplate.rejected, (state) => {
        state.loading = false;
      })

      .addCase(updateJurnalTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateJurnalTemplate.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateJurnalTemplate.rejected, (state) => {
        state.loading = false;
      })

      .addCase(deleteJurnalTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteJurnalTemplate.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteJurnalTemplate.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { clearSelectedJurnalTemplate } = JurnalTemplateSlice.actions;

export default JurnalTemplateSlice.reducer;
